import { useEffect, useMemo, useRef, useState } from 'react';
import { Funnel, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Popup, Sheet, TextInput } from '@gravity-ui/uikit';
import type { MRT_Column } from 'material-react-table';
import { useGetEstablishmentsQuery } from 'store/api';
import { skipToken } from '@reduxjs/toolkit/query';
import styles from '../custom-establishment-filter/styles.module.scss';

interface Person {
	id: number;
	email: string;
	role: string;
	firstName: string;
	middleName: string;
	lastName: string;
	establishment: {
		id?: number;
		name?: string | null;
	} | null;
	imageUrl: string;
	region: {
		code?: number;
		fullname: string;
		guid?: string;
		name?: string;
	};
	specialty: string;
	active: boolean;
	createdDate: string;
}

interface CustomEstablishmentFilterSheetProps {
	column?: MRT_Column<Person>;

	onApplyFilters?: (establishments: string[]) => void;

	selectedFilters: string[];
	resetFilters: () => void;
}

export const CustomEstablishmentsSheetFilter = ({
	column,
	selectedFilters,
	onApplyFilters,
	resetFilters,
}: CustomEstablishmentFilterSheetProps) => {
	const [open, setOpen] = useState(false);
	const buttonRef = useRef(null);

	const [searchValue, setSearchValue] = useState('');
	const [selectedEstablishments, setSelectedEstablishments] = useState<string[]>(
		(column?.getFilterValue() as string[]) || [],
	);

	const { data: estList } = useGetEstablishmentsQuery(
		{ pageNumber: 0, pageSize: 100, sort: 'DESC', sortField: 'id' } ?? skipToken,
	);

	useEffect(() => {
		setSelectedEstablishments(selectedFilters || []);
	}, [selectedFilters]);

	const allEstablishments = useMemo(() => {
		return Array.from(new Set(estList?.content.map((p) => p?.name?.trim()).filter((n): n is string => Boolean(n))));
	}, [estList?.content]);

	const filteredEstablishments = useMemo(() => {
		return allEstablishments.filter((name) => name.toLowerCase().includes(searchValue.toLowerCase()));
	}, [allEstablishments, searchValue]);

	const handleCheckboxChange = (estName: string) => {
		setSelectedEstablishments((selectedEstablishments) =>
			selectedEstablishments.includes(estName)
				? selectedEstablishments.filter((v) => v !== estName)
				: [...selectedEstablishments, estName],
		);
	};

	const handleApply = () => {
		if (column) {
			column.setFilterValue(selectedEstablishments);
		}

		if (onApplyFilters) {
			onApplyFilters(selectedEstablishments);
		}
		setOpen(false);
	};

	return (
		<>
			<Button onClick={() => setOpen(true)} view="flat" size="m">
				<Icon data={Funnel} size={18} />
			</Button>

			<Sheet visible={open} onClose={() => setOpen(false)} title="Учреждения" className={styles.sheet}>
				<div className={styles.sheet__close}>
					<Button onClick={() => setOpen(false)} view="flat">
						<Icon data={Xmark} size={16} />
					</Button>
				</div>

				<div className={styles.popup_container}>
					<div className={styles.popup_container__input}>
						<TextInput
							type="text"
							placeholder="Поиск учреждений..."
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</div>

					<div className={styles.popup_container__list}>
						{filteredEstablishments.map((estName) => {
							const checked = selectedEstablishments.includes(estName);
							return (
								<div key={estName} className={styles.popup_container__list__establishment}>
									<span>{estName}</span>
									<input
										type="checkbox"
										checked={checked}
										onChange={() => handleCheckboxChange(estName)}
										className={styles.popup_container__list__establishment__checkbox}
									/>
								</div>
							);
						})}
					</div>

					<div className={styles.popup_container__btn}>
						<Button onClick={handleApply} size="l" className={styles.popup_container__btn__apply}>
							Применить
						</Button>

						<Button
							size="l"
							view="flat"
							className={styles.popup_container__btn__reset}
							onClick={() => {
								resetFilters();
								setOpen(false);
							}}
						>
							Сбросить
						</Button>
					</div>
				</div>
			</Sheet>
		</>
	);
};
